import {Component, EventEmitter, Input, Output, inject} from '@angular/core';
import {CommonModule} from '@angular/common';
import {Router} from '@angular/router';
import {faCode, faRotateRight} from '@fortawesome/free-solid-svg-icons';
import {FontAwesomeModule} from '@fortawesome/angular-fontawesome';
import {marked} from 'marked';
import {
  ChatMessage,
  ClarifyOption,
  RecipeDraftPayload,
  RecipeMatchesArtifactData,
  RecipeMatchItem,
  RecipeSuggestionItem,
  RecipeSuggestionsArtifactData,
} from '../../../services/dialog/dialog.models';
import {ClarifyOptionsComponent} from '../clarify-options/clarify-options.component';
import {ConfirmActionComponent} from '../confirm-action/confirm-action.component';
import {UiRecipePreviewComponent} from '../../../shared/ui';

// Single newline in the backend's `message` is meant as a line break (see the mock
// backend's multi-line location answer), which bare CommonMark otherwise ignores
// inside a paragraph.
marked.setOptions({breaks: true});

@Component({
  selector: 'app-chat-message',
  standalone: true,
  imports: [CommonModule, FontAwesomeModule, ClarifyOptionsComponent, ConfirmActionComponent, UiRecipePreviewComponent],
  templateUrl: './chat-message.component.html',
})
export class ChatMessageComponent {
  private readonly router = inject(Router);

  @Input({required: true}) message!: ChatMessage;
  @Input() debugActive = false;
  @Output() optionSelected = new EventEmitter<ClarifyOption>();
  @Output() confirmAnswered = new EventEmitter<'yes' | 'no'>();
  @Output() retry = new EventEmitter<void>();
  @Output() turnDetailRequested = new EventEmitter<ChatMessage>();
  /** Emitted when the user clicks "Rezept speichern" on a `recipeJob` draft card —
   *  see ChatMessage.recipeJob's doc comment for why this is a direct save, not the
   *  generic confirm/`confirmAnswered` path. */
  @Output() saveRecipeJob = new EventEmitter<{messageId: string; jobId: string}>();
  /** The user tapped a brainstorm idea card to author it "for real" — the panel
   *  hands this to DialogService.authorSuggestion (which sends the whole idea as
   *  `selected_idea` to propose_recipe). */
  @Output() authorSuggestion = new EventEmitter<RecipeSuggestionItem>();

  faRotateRight = faRotateRight;
  faCode = faCode;

  /** Local optimistic lock so a slow save can't be double-clicked — reset is
   *  unnecessary: once saved, `message.recipeJob.status` moves off `draft_ready` and
   *  this whole card is replaced by the "saved" one, so a stale `true` here never
   *  becomes visible again. */
  savingRecipeJob = false;

  onSaveRecipeJobClick(): void {
    const job = this.message.recipeJob;
    if (!job || job.status !== 'draft_ready' || this.savingRecipeJob) return;
    this.savingRecipeJob = true;
    this.saveRecipeJob.emit({messageId: this.message.id, jobId: job.jobId});
  }

  openSavedRecipeJob(): void {
    const id = this.message.recipeJob?.createdRecipeId;
    if (id != null) void this.router.navigate(['/recipe', id]);
  }

  /** True for any turn (draft-confirm or completed) that carries a `create_recipe`
   *  action — gates the recipe draft card and the "Rezept speichern" button label. */
  get isCreateRecipeAction(): boolean {
    return this.message.action?.type === 'create_recipe';
  }

  /** `action.payload` is an untyped `Record<string, unknown>` on the wire; this cast
   *  is the one place that assumes the `create_recipe` payload shape, gated by
   *  `isCreateRecipeAction` above. */
  get recipeDraftPayload(): RecipeDraftPayload | null {
    if (!this.isCreateRecipeAction) return null;
    return this.message.action!.payload as unknown as RecipeDraftPayload;
  }

  get confirmLabel(): string {
    return this.isCreateRecipeAction ? 'Rezept speichern' : 'Ja';
  }

  /** Only set once the recipe has actually been saved — prefers the id embedded in
   *  the payload, falls back to `affected` (where `resource === 'recipe'`) since the
   *  backend contract documents both as valid sources. */
  get savedRecipeId(): number | null {
    if (!this.isCreateRecipeAction || !this.message.action!.executed) return null;

    const fromPayload = this.recipeDraftPayload?.recipe_id;
    if (typeof fromPayload === 'number') return fromPayload;

    const fromAffected = this.message.affected
      ?.find(a => a.resource === 'recipe')
      ?.entity_ids?.[0];
    return typeof fromAffected === 'number' ? fromAffected : null;
  }

  openRecipe(): void {
    const id = this.savedRecipeId;
    if (id != null) void this.router.navigate(['/recipe', id]);
  }

  /** Brainstorm ideas surfaced by get_recipe_suggestions (artifact `recipe_suggestions`). */
  get recipeSuggestions(): RecipeSuggestionItem[] {
    const a = this.message.artifacts?.find(x => x.type === 'recipe_suggestions');
    return a ? ((a.data as RecipeSuggestionsArtifactData).suggestions ?? []) : [];
  }

  /** Saved-recipe matches surfaced by search_recipes (artifact `recipe_matches`). */
  get recipeMatches(): RecipeMatchItem[] {
    const a = this.message.artifacts?.find(x => x.type === 'recipe_matches');
    return a ? ((a.data as RecipeMatchesArtifactData).recipes ?? []) : [];
  }

  openRecipeById(id: number): void {
    void this.router.navigate(['/recipe', id]);
  }

  get isSystem(): boolean {
    return this.message.role === 'system';
  }

  /** Only the assistant's `message.text` can carry backend-authored markdown — user
   *  input and the client's own error strings are rendered as plain text. Angular
   *  sanitizes the `innerHTML` binding itself (stripping scripts/handlers/`javascript:`
   *  URLs), so no separate sanitizer step is needed here. */
  get isMarkdown(): boolean {
    return this.message.role === 'assistant';
  }

  get renderedMarkdown(): string {
    return marked.parse(this.message.text, {async: false});
  }

  get bubbleClass(): string {
    switch (this.message.role) {
      case 'user':
        return 'chat-bubble-primary';
      case 'error':
        return 'chat-bubble-error';
      default:
        return '';
    }
  }
}
